import React, { useState, useContext, useEffect } from 'react';
import {
    Button,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { styled } from '@mui/system';
import { useNavigate, useLocation } from 'react-router-dom';
import { OnboardingLayout } from './onboard-layout';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import SubscriptionCard from '../../UIControls/SubscriptionCard';
import subscriptionPlans from '../../authenticated/settings/subscriptionPlans.js';
import StoreContext from '../../../react-core-lib/components/app/store-context.js';
import config from '../../../config';
import getDiscountByPlan from '../../helper/getDiscountHelper.js';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    borderRadius: 50,
    overflow: 'hidden',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.12)',
    width: 205,
}));

const StyledToggleButton = styled(ToggleButton)(({ theme, selected }) => ({
    color: selected ? '#fff' : '#000',
    backgroundColor: selected ? '#000453' : '#fff',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: selected ? '#000453' : '#f4f4f4',
    },
    '&.Mui-selected': {
        backgroundColor: '#000453',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#000453',
        },
    },
    border: 'none',
    borderRadius: 0,
    flex: 1,
}));

const OnboardPlanScreen = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const location = useLocation();
    const planKeys = Object.keys(subscriptionPlans);
    const appStateStore = useContext(StoreContext);
    const { authStore, userDataStore } = useContext(StoreContext);
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const [userData, setUserData] = useState({ ...location.state?.userData });
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showEnterpriseDialog, setShowEnterpriseDialog] = useState(false);
    // const isShowOffer = localStorage.getItem('LVS') === 'true'; // Blocked LVS regarding CETRA-371
    const isShowOffer = false;
    const [billingCycle, setBillingCycle] = useState('monthly');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const showAnnualDiscountText = billingCycle === 'annually';
    const [discounts, setDiscounts] = useState({});
    const subscriptionEndDate = userDataStore?.subscriptionData?.subscriptionEndDate;

    const hasSubData =
        userDataStore?.subscriptionData != null &&
        Object.prototype.hasOwnProperty.call(userDataStore.subscriptionData, 'subscriptionEndDate');

    const isUserDataReady = !!userDataStore?.userData?.emailAddress && hasSubData;
    const isTrialExpired = typeof subscriptionEndDate === 'number' ? Date.now() > subscriptionEndDate : false;

    useEffect(() => {
        localStorage.setItem('onboardingComplete', 'true');
    }, []);

    const handleBack = async () => {
        await authStore.logout();
        await appStateStore.clearUserStores();
        localStorage.clear();
        navigate(`/${window.location.search || ''}`);
    };

    const handleCardClick = (plan) => {
        if (isMultiUser) return;

        console.warn(plan);
        setSelectedPlan(plan);
    };

    const handleBillingCycleChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const handleNext = () => {
        let finalPlan = selectedPlan;
        console.log('handleNext: selectedPlan =', selectedPlan);
        // If user picks Enterprise => show the new "Excellent choice!" dialog
        if (selectedPlan === 'Enterprise') {
            setShowEnterpriseDialog(true);
            return;
        }

        const path = isShowOffer ? '/checkout?LVS=true' : '/checkout';
        navigate(path, { state: { userData, plan: finalPlan, billingCycle: billingCycle } });
    };

    useEffect(() => {
        const fetchDiscounts = async () => {
            const newDiscounts = {};

            for (const plan of planKeys) {
                const discount = await getDiscountByPlan(authStore?.idToken, plan);
                // getDiscountByPlan returns a percentage number, so wrap it in our new format
                newDiscounts[plan] = discount ? { type: 'percent', value: discount } : null;
            }

            console.warn('Fetched Discounts: ', newDiscounts);
            setDiscounts(newDiscounts);
        };

        fetchDiscounts();
    }, [authStore?.idToken]);

    // Tests
    // useEffect(() => {
    //     // remove after debugging
    //     console.log('READY?', {
    //         email: userDataStore?.userData?.emailAddress,
    //         subData: userDataStore?.subscriptionData,
    //     });
    // }, [userDataStore?.userData?.emailAddress, userDataStore?.subscriptionData]);

    const isNextButtonDisabled = selectedPlan === null;

    const inputChildren = (
        <>
            {!isShowOffer && !isTrialExpired && (
                <>
                    <SubscriptionCard
                        plan={'Professional - 14 day FREE trial'}
                        billingCycle={billingCycle}
                        showSelect={true}
                        sx={{ color: 'white!important' }}
                    />
                    <Divider sx={{ mb: 4 }} />
                </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ maxWidth: 400 }}>
                    Opt for our annual plan for an extra saving! Enjoy additional seats, more features and priority
                    support all year long.
                </Typography>
                <StyledToggleButtonGroup
                    value={billingCycle}
                    exclusive
                    onChange={handleBillingCycleChange}
                    aria-label="billing cycle"
                    sx={{ marginTop: isMobile ? 2 : 0 }}
                >
                    <StyledToggleButton value="monthly" aria-label="monthly" selected={billingCycle === 'monthly'}>
                        Monthly
                    </StyledToggleButton>
                    <StyledToggleButton value="annually" aria-label="annually" selected={billingCycle === 'annually'}>
                        Annually
                    </StyledToggleButton>
                </StyledToggleButtonGroup>
                {showAnnualDiscountText && (
                    <Typography
                        variant="body1"
                        sx={{ fontSize: '0.875rem', textAlign: 'right', flex: 1, marginLeft: 2 }}
                    >
                        {config.annualDiscountText}
                    </Typography>
                )}
            </Box>

            {isMultiUser && (
                <Box
                    sx={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: 2,
                        mb: 2,
                        mt: 3,
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#555' }}>
                        ⚠️ You’re currently logged in as a clinic team member. Only the Administrator can manage or
                        change the subscription plan.
                    </Typography>
                </Box>
            )}

            <Box sx={{ textAlign: 'left', marginTop: '24px' }}>
                {planKeys
                    .filter((plan) => !plan.includes('trial'))
                    .map((plan, index) => (
                        <Box key={index} onClick={() => handleCardClick(plan)} sx={{ cursor: 'pointer' }}>
                            <SubscriptionCard
                                plan={plan}
                                billingCycle={billingCycle}
                                discount={isShowOffer && billingCycle === 'annually' ? { type: 'percent', value: 73 } : discounts[plan]}
                                disabled={isMultiUser}
                                sx={{
                                    background:
                                        plan === selectedPlan
                                            ? 'linear-gradient(to right, #ff4081, #ff80ab)'
                                            : 'transparent',
                                    padding: plan === selectedPlan ? '5px' : '0',
                                }}
                            />
                        </Box>
                    ))}
            </Box>
        </>
    );

    const renderButtonGroup = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: 'auto',
                padding: '24px 0',
            }}
        >
            <Box onClick={handleBack} sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
                <KeyboardBackspaceIcon />
                <Button
                    sx={{
                        color: '#000453',
                        textDecoration: 'underline',
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Go back
                </Button>
            </Box>
            <PinkGradientButton
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={isNextButtonDisabled}
                sx={{ flexGrow: 1, marginLeft: 1, position: 'relative', maxWidth: 200, m: 0 }}
            >
                Next
            </PinkGradientButton>
        </Box>
    );

    return !isUserDataReady ? (
        <BlockingSpinner open={true} />
    ) : (
        <OnboardingLayout
            title="Here's what we recommend"
            subtitle={
                !isShowOffer && !isTrialExpired
                    ? 'Try out our professional plan which is free for 14-days, or dive right in and subscribe to your preferred plan.'
                    : ''
            }
            previousStepPath="/onboard/code"
            inputChildren={inputChildren}
            navChildren={renderButtonGroup()}
            currentStep={5}
            userData={userData}
            backgroundColor={false}
        >
            {/* 4) Our new Enterprise ConfirmationDialog with 2 buttons */}
            <ConfirmationDialog
                open={showEnterpriseDialog}
                handleClose={() => setShowEnterpriseDialog(false)}
                // 2-button scenario:
                cancelTextReq={true}
                cancelText="No, go back"
                confirmText="Enjoy your continued trial"
                title="Excellent choice!"
                message={
                    <>
                        Let’s explore this further. Please email us at{' '}
                        <a
                            href="mailto:hello@cetravets.com"
                            style={{
                                fontWeight: 'bold',
                                color: '#ff4081',
                                textDecoration: 'none',
                            }}
                        >
                            hello@cetravets.com
                        </a>{' '}
                        detailing the type of enterprise package you’re interested in. If preferred, we can schedule a
                        meeting to discuss the finer details.
                    </>
                }
                onConfirm={async () => {
                    try {
                        // 0) If user is on trial, we cannot provision Enterprise => show warning and exit
                        const { trialActive, trialEndDate: existingTrialEndDate } =
                            userDataStore?.subscriptionData || {};
                        const now = Date.now();
                        if (!trialActive && existingTrialEndDate && now > existingTrialEndDate) {
                            console.warn('Cannot restart trial — already expired.');
                            setShowEnterpriseDialog(false);
                            return;
                        }

                        // 1) Same logic as "onConfirmTrial" in SubscriptionCard I hope so.
                        const trialEndDate = new Date();
                        trialEndDate.setDate(trialEndDate.getDate() + 14);
                        const trial_end = Math.floor(trialEndDate.getTime());

                        const subscriptionData = {
                            accountType: 'Professional', // as consistancy to the other Trial
                            trialActive: true,
                            trialEndDate: trial_end,
                            subscriptionEndDate: trial_end,
                            cancelled: false,
                        };

                        // 2) Update the user data
                        await UserApi.updateClinicianData(authStore.idToken, { subscriptionData });

                        // 3) Update local store so keep the user is on trial
                        userDataStore.setUserData({
                            ...userDataStore.getUserData(),
                            subscriptionData,
                        });

                        // 4) Set localStorage to trigger the green success message on Home screen
                        localStorage.setItem('trialSuccess', 'true');

                        // 5) auth
                        authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);

                        // 6) Klaviyo analytics (as trial)
                        await KlaviyoApi.updateUserInKlaviyo(
                            authStore.idToken,
                            userDataStore.userData.emailAddress,
                            'Pro Plan Trial 14d',
                        );
                    } catch (err) {
                        console.error('Error provisioning Enterprise => trial:', err);
                    }

                    // 7) Finally close dialog & go Home
                    setShowEnterpriseDialog(false);
                    navigate('/');
                }}
            />
        </OnboardingLayout>
    );
};

export default OnboardPlanScreen;
